import { Suspense } from 'react';
import { Navigate, Route } from 'react-router-dom';
import ErrorBoundary from '@/components/ErrorBoundary';
import { LoadingSpinner } from '@/app/LoadingSpinner';
import { StockModeLanding } from '@/features/stock-mode/StockModeLanding';
import {
  DashboardPermissionGuard,
  FeatureGuard,
  ModuleGuard,
  P,
  PFull,
  PNoHeader,
} from '@/app/guards';
import {
  Avoirs,
  BonsDeLivraison,
  BonsEnCours,
  BonsReduction,
  Caisse,
  CaisseRapide,
  CaisseRestaurant,
  Clients,
  Dashboard,
  DashboardKPI,
  Duplicata,
  Encours,
  EtatStocks,
  Etiquettes,
  Fournisseurs,
  Integrer,
  NoteDeFrais,
  Inventaires,
  ModeStock,
  LignesVente,
  MouvementsStock,
  PiecesCommerciales,
  Produits,
  Rapports,
  ResellerPortal,
  SessionsCaisse,
  TypesFidelite,
  Ventes,
} from '@/app/lazyPages';

/**
 * Groupe de routes extrait d'App.tsx (strangler 1b) — ordre source préservé.
 * Table de routes déclarative : dépasse volontairement max-lines-per-function (warn).
 */
export function coreRoutes() {
  return (
    <>
      <Route
        path="/reseller-portal"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <ResellerPortal />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/dashboard"
        element={
          <P>
            <StockModeLanding>
              <DashboardPermissionGuard>
                <ErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Dashboard />
                  </Suspense>
                </ErrorBoundary>
              </DashboardPermissionGuard>
            </StockModeLanding>
          </P>
        }
      />
      <Route
        path="/dashboard/kpi"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <DashboardKPI />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/caisse"
        element={
          <PNoHeader>
            <FeatureGuard feature="caisse_classique" redirectTo="/caisse-rapide">
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <Caisse />
                </Suspense>
              </ErrorBoundary>
            </FeatureGuard>
          </PNoHeader>
        }
      />
      <Route
        path="/bons-en-cours"
        element={
          <P>
            <ModuleGuard modules={['mod_bons_provisoires']}>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <BonsEnCours />
                </Suspense>
              </ErrorBoundary>
            </ModuleGuard>
          </P>
        }
      />
      <Route
        path="/caisse-rapide"
        element={
          <PNoHeader>
            <ModuleGuard modules={['mod_caisse_rapide']}>
              <Suspense fallback={<LoadingSpinner />}>
                <CaisseRapide />
              </Suspense>
            </ModuleGuard>
          </PNoHeader>
        }
      />
      <Route
        path="/caisse-restaurant"
        element={
          <PFull>
            <ModuleGuard modules={['mod_restaurant_salle']}>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <CaisseRestaurant />
                </Suspense>
              </ErrorBoundary>
            </ModuleGuard>
          </PFull>
        }
      />
      <Route path="/caisse-food" element={<Navigate to="/caisse" replace />} />
      <Route
        path="/produits"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <Produits />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/integrer"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <Integrer />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/ventes"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <Ventes />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/rapports"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <Rapports />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/clients"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <Clients />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/fournisseurs"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <Fournisseurs />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/etiquettes"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <Etiquettes />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/avoirs"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <Avoirs />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/duplicata"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <Duplicata />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/note-de-frais"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <NoteDeFrais />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/encours"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <Encours />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/bons-de-livraison"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <BonsDeLivraison />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/pieces-commerciales"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <PiecesCommerciales />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/lignes-vente"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <LignesVente />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/sessions-caisse"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <SessionsCaisse />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/inventaire"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <EtatStocks />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/mouvements"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <MouvementsStock />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/mode-stock"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <ModeStock />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/inventaires"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <Inventaires />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/fidelite"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <TypesFidelite />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
      <Route
        path="/bons-reduction"
        element={
          <P>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <BonsReduction />
              </Suspense>
            </ErrorBoundary>
          </P>
        }
      />
    </>
  );
}
